import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { ButtonGroup, Flex, Button } from '@cad-arc';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useQueueStore } from '../queues/context/use-queue-store';
import { globalAttributes } from '../common/global-attributes';
import { FrPageHeader } from '../fr-page-layout/fr-page-header';
import { FrSortMenu } from '../fr-list/components/fr-sort-menu';
import { FrFilterMenu } from './fr-filter-menu';
import { clearWindowHistoryState } from '../common/helpers/utils';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { FrQueueSearchInput } from '../fr-units-list/fr-units-list-page/fr-queue-search-input';
import { FrBoloListHeaderButton } from './bolo-list/fr-bolo-list-header-button';
import { useFrEventsListTab } from './fr-events-list-context';
import { useEventSortColumns } from './use-event-sort-columns';
import { EventListTabType } from '../queues/types';
import { FrQueueOtherOptionsMenu } from '../common/components/fr-queues/fr-queue-other-options-menu';

export const FrEventsListHeader = observer(function FrEventListHeader() {
    const location = useLocation();
    const { userAgencyTypeGlobalAttrId } = useBootstrapState();
    const eventQueueStore = useQueueStore();
    const eventSortColumns = useEventSortColumns();
    const {
        viewBolos,
        firstResponderViewAssignedEventsEnabled,
        firstResponderViewUnassignedEventsEnabled,
    } = useEnabledFeatures();

    const { tab, setTab } = useFrEventsListTab();

    useEffect(
        function handleShortcutTabSelection() {
            if (location.state?.tab) {
                setTab(location.state.tab as EventListTabType);
            }
            return function handleShortcutTabSelectionCleanup() {
                clearWindowHistoryState();
            };
        },
        [location.state?.tab, setTab]
    );

    const countedGroups = Object.fromEntries(
        eventQueueStore.listStore.data.groupHeaders.map((header, index) => [
            header,
            eventQueueStore.listStore.data.groupCounts[index],
        ])
    );

    const dispatched = countedGroups.Dispatched ?? 0;
    const pending = countedGroups.Pending ?? 0;
    const groupCount = {
        all: dispatched + pending,
        dispatched,
        pending,
    };

    const title =
        userAgencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE
            ? 'Events'
            : 'Fire and EMS Events';

    return (
        <FrPageHeader title={title} showGoBackButton>
            <Flex flex="1 1 70%" px={4}>
                <Flex gap={3} justifyContent="space-between" flex="1 1 100%" alignItems="center">
                    <ButtonGroup isAttached>
                        <Button
                            onClick={() => setTab('ALL')}
                            isActive={tab === 'ALL'}
                            data-testid="fr-all-events-tab-btn"
                        >
                            All Events ({groupCount.all})
                        </Button>
                        {firstResponderViewUnassignedEventsEnabled && (
                            <Button
                                onClick={() => setTab('UNASSIGNED')}
                                isActive={tab === 'UNASSIGNED'}
                                data-testid="fr-pending-events-tab-btn"
                            >
                                Pending ({groupCount.pending})
                            </Button>
                        )}
                        {firstResponderViewAssignedEventsEnabled && (
                            <Button
                                onClick={() => setTab('ASSIGNED')}
                                isActive={tab === 'ASSIGNED'}
                                data-testid="fr-dispatched-events-tab-btn"
                            >
                                Dispatched ({groupCount.dispatched})
                            </Button>
                        )}
                        {viewBolos && (
                            <FrBoloListHeaderButton
                                isActive={tab === 'BOLO'}
                                onClick={() => setTab('BOLO')}
                            />
                        )}
                    </ButtonGroup>
                    <Flex gap={3} alignItems="center">
                        <FrQueueSearchInput searchButtonVariant="outline" />
                        <FrQueueOtherOptionsMenu />
                        <FrFilterMenu />
                        <FrSortMenu defaultColumnDefinition="PRIORITY" columns={eventSortColumns} />
                    </Flex>
                </Flex>
            </Flex>
        </FrPageHeader>
    );
});
